import { ArrowRight, Database, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Pill } from "@/components/brand/Pill";
import { Pill as CardPill, ScardShell } from "@/components/projects/project-card-shell";
import { useCurrentUser } from "@/hooks/useAuth";
import { useProjectCurrentLoad } from "@/hooks/useApi";
import { formatDateTime, formatRelative } from "@/lib/datetime";
import { canReachProjectRoute } from "@/lib/project-nav";
import {
  SOURCE_TYPE_SHORT,
  loadRows,
  shownLoad,
  sourceLabel,
  sourceLoadState,
  stateNote,
  statePill,
} from "@/lib/sourceLoad";
import type { Project } from "@/types/api";

const ACCENT = { ok: "#27a155", gold: "#ffc83d", danger: "#b02a31", muted: "#c5ccd6" } as const;

/**
 * Cartão de origem da Visão geral (#1785): a origem do projeto e a carga vigente do ex-kit, no
 * lugar das conexões de origem. Navega para a página Conexões, que é a casa da origem (#428).
 */
export function SourceLoadOverviewCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useProjectCurrentLoad(project.id);
  const state = sourceLoadState(data, isLoading, isError);
  const pill = statePill(t, state);
  const load = shownLoad(data);
  const short = project.source_type ? SOURCE_TYPE_SHORT[project.source_type] : "ERP";

  return (
    <ScardShell
      to={`/projects/${project.id}/connections#source-systems`}
      overline={t("Source System")}
      title={t("Extraction kit load")}
      pill={
        state === "loading" ? undefined : (
          <CardPill tone={pill.tone === "ok" ? "ok" : "gold"}>{pill.label}</CardPill>
        )
      }
      stat={
        <div className="flex items-baseline gap-2.5">
          <span className="font-brand font-extrabold text-[56px] leading-none tracking-[-0.035em] text-[#08254c]">
            {short}
          </span>
          <span className="font-brand font-normal italic text-[22px] leading-[1.1] tracking-[-0.015em] text-[#08254c]">
            → S/4HANA
          </span>
        </div>
      }
      meta={loadRows(t, project, load).slice(0, 4)}
      cta={
        <>
          <span className="flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5" /> {t("View source")}
          </span>
          <ArrowRight className="w-3.5 h-3.5" />
        </>
      }
    />
  );
}

/**
 * A origem na página Conexões (#1785): o que o projeto tem dela é a carga do ex-kit. Mostra a
 * vigente, avisa da que está chegando e, para quem alcança as Configurações, leva à origem do
 * projeto e às chaves de API do ex-kit.
 */
export function SourceLoadPanel({ project }: { project: Project }) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const { data, isLoading, isError } = useProjectCurrentLoad(project.id);
  const state = sourceLoadState(data, isLoading, isError);
  const pill = statePill(t, state);
  const note = stateNote(t, state, data);
  const load = shownLoad(data);
  const showSettings = canReachProjectRoute(me?.role, "settings");

  return (
    <article
      data-testid="source-load-panel"
      className="relative bg-white border border-[#e8ebf0] rounded-xl p-5 flex flex-col gap-3.5 overflow-hidden"
    >
      <span
        className="absolute left-0 top-5 w-8 h-[3px] rounded-r-sm"
        style={{ background: ACCENT[pill.tone as keyof typeof ACCENT] ?? ACCENT.muted }}
      />
      <header className="flex justify-between items-start gap-3.5">
        <div className="flex gap-3.5 items-start min-w-0">
          <div className="w-11 h-11 rounded-[10px] flex items-center justify-center text-white shrink-0 bg-[linear-gradient(135deg,#0073e6,#25a9e0)]">
            <Database className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-brand font-bold text-base text-[#08254c] m-0 mb-0.5 tracking-[-0.01em]">
              {t("Extraction kit load")}
            </h3>
            <div className="font-mono text-[10px] text-[#7b8799] tracking-[0.08em] uppercase">
              {sourceLabel(t, project)}
            </div>
          </div>
        </div>
        {state !== "loading" && <Pill tone={pill.tone}>{pill.label}</Pill>}
      </header>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2.5 py-3.5 border-y border-[#f2f4f7]">
        {loadRows(t, project, load).map((r) => (
          <div key={r.k} className="min-w-0">
            <div className="font-mono text-[9px] text-[#7b8799] tracking-[0.12em] uppercase mb-0.5">
              {r.k}
            </div>
            <div
              title={r.v}
              className={"text-xs text-[#08254c] font-medium truncate" + (r.mono ? " font-mono" : "")}
            >
              {r.v}
            </div>
          </div>
        ))}
      </div>
      <footer className="flex justify-between items-center gap-2 flex-wrap">
        <div className="text-xs text-[#596b85]" data-testid="source-load-note">
          {note ??
            (load?.closed_at ? (
              <span title={formatDateTime(load.closed_at)}>
                {t("Received {{when}}", { when: formatRelative(load.closed_at) })}
              </span>
            ) : null)}
        </div>
        {showSettings && (
          <Link
            to={`/projects/${project.id}/settings#source-extraction`}
            className="text-xs font-semibold text-[#0073e6] hover:underline flex items-center gap-1.5"
          >
            <Settings className="w-3.5 h-3.5" /> {t("Source settings and API keys")}
          </Link>
        )}
      </footer>
    </article>
  );
}
